import { Link, useParams, useSearchParams } from "react-router-dom";
import AnnouncementsFeed from "../../components/comms/AnnouncementsFeed";
import { Building2, Layers, MapPin, MessageSquareText, Star } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { Loader, ErrorState } from "../../components/common/States";
import FeedbackForm from "../../components/feedback/FeedbackForm";
import { ORG_TYPE } from "../../lib/labels";

export const RatingSummary = ({ company }) => company.review_count > 0 ? (
  <p className="flex items-center gap-1.5 text-sm" data-testid="company-rating-summary">
    <Star className="h-4 w-4 fill-amber-400 text-amber-400" /><b>{company.avg_rating}</b><span className="text-slate-500">/ 5 · {company.review_count} avis publié(s)</span>
  </p>
) : <p className="text-sm text-slate-500" data-testid="company-rating-summary">Aucun avis publié pour le moment</p>;

const Structure = ({ agencies, services }) => (
  <div className="space-y-6" data-testid="company-structure">
    <div>
      <p className="eyebrow mb-3 flex items-center gap-2"><Building2 className="h-3.5 w-3.5" />Agences ({agencies.length})</p>
      {!agencies.length && <p className="text-sm text-slate-500">Aucune agence renseignée.</p>}
      <ul className="space-y-2">
        {agencies.map((a) => (
          <li key={a.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid={`public-agency-${a.id}`}>
            <p className="font-medium">{a.name}</p>
            <p className="text-slate-500">{[a.address, a.city, a.country].filter(Boolean).join(", ") || "Adresse non renseignée"}</p>
            <p className="mt-1 text-xs text-slate-400">{services.filter((s) => s.agency_id === a.id).map((s) => s.name).join(" · ")}</p>
          </li>
        ))}
      </ul>
    </div>
    <div>
      <p className="eyebrow mb-3 flex items-center gap-2"><Layers className="h-3.5 w-3.5" />Services ({services.length})</p>
      <div className="flex flex-wrap gap-2">
        {services.map((s) => <span key={s.id} title={s.description || ""} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{s.name}</span>)}
        {!services.length && <p className="text-sm text-slate-500">Aucun service renseigné.</p>}
      </div>
    </div>
  </div>
);

export const CompanyFormLayout = ({ data, qr, source, preset }) => (
  <div className="mx-auto grid max-w-6xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-14">
    <aside className="space-y-6 lg:col-span-4">
      <div>
        <p className="eyebrow">{ORG_TYPE[data.company.org_type]} · {data.company.sector}</p>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight" data-testid="company-public-name">{data.company.name}</h1>
        <p className="mt-2 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-4 w-4" />{data.company.city}, {data.company.country}</p>
        <div className="mt-3"><RatingSummary company={data.company} /></div>
        {qr && <p className="mt-4 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="qr-label">Point de collecte : {qr.label}</p>}
        {qr?.message && <p className="mt-3 text-sm italic text-slate-600" data-testid="qr-public-message">« {qr.message} »</p>}
        {data.company.description && <p className="mt-4 text-sm leading-relaxed text-slate-600">{data.company.description}</p>}
        <div className="mt-5"><AnnouncementsFeed url={`/public/companies/${data.company.slug}/announcements`} testId="public-announcements" /></div>
        <Button asChild variant="outline" size="sm" className="mt-4 rounded-full" data-testid="company-reviews-link">
          <Link to={`/e/${data.company.slug}/avis`}><MessageSquareText className="mr-1.5 h-4 w-4" />Lire les avis publics</Link>
        </Button>
      </div>
      {!qr && <Structure agencies={data.agencies} services={data.services} />}
    </aside>
    <section className="lg:col-span-8" id="deposer">
      <h2 className="mb-4 font-display text-lg font-semibold">Déposer un retour</h2>
      <FeedbackForm company={data.company} agencies={data.agencies} services={data.services}
        preset={qr ? { agency_id: qr.agency_id, service_id: qr.service_id } : (preset || {})} qrCode={qr?.code} source={source} sectorForm={data.sector_form} />
    </section>
  </div>
);

export default function CompanyProfile() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { data, loading, error } = useApi(`/public/companies/${slug}`);
  if (loading) return <Loader />;
  if (error) return <div className="mx-auto max-w-lg px-4 py-16"><ErrorState message={error} /></div>;
  const agency = data.agencies.find((a) => a.id === params.get("agence"));
  const service = data.services.find((s) => s.id === params.get("service"));
  const preset = { agency_id: agency?.id || "", service_id: service && (!service.agency_id || service.agency_id === agency?.id) ? service.id : "" };
  return <CompanyFormLayout data={data} preset={preset} source={params.get("src") === "annuaire" ? "directory" : "web"} />;
}
